import { lessonsFor, mc, tf } from "./helpers";
import type { LessonContentMeta } from "./types";

const guide = { label: "AI Safety Guide", href: "/security-guide" };
const checklist = { label: "Security Checklist", href: "/security-checklist" };
const studio = { label: "Prompt Studio", href: "/prompt-studio" };
const chat = { label: "Chat with GenieBot", href: "/chat" };
const pw = { label: "All tools (password generator)", href: "/tools" };
const blogs = { label: "Articles", href: "/blogs" };

const meta = (freshness: LessonContentMeta["freshness"], sources: LessonContentMeta["sources"]): LessonContentMeta => ({
  contentVersion: "1.0",
  author: "GenieBot Learning",
  lastReviewedAt: "2026-10-12",
  nextReviewAt: "2027-04-12",
  freshness,
  sources,
});
const NIST = { title: "AI Risk Management Framework", organization: "NIST", url: "https://www.nist.gov/itl/ai-risk-management-framework" };
const CISA = { title: "Secure Our World", organization: "CISA", url: "https://www.cisa.gov/secure-our-world" };
const FTC = { title: "Consumer advice", organization: "FTC", url: "https://consumer.ftc.gov/" };
const OPENAI = { title: "Prompt engineering guide", organization: "OpenAI", url: "https://platform.openai.com/docs/guides/prompt-engineering" };

/** Everyday, high-value lessons added in round 2 stage 4. */
export const AI_SAFETY_EVERYDAY = lessonsFor("ai-safety", [
  {
    slug: "health-money-legal",
    title: "Using AI for Health, Money and Legal Questions",
    minutes: 5,
    learn: ["Use AI to prepare, not to decide", "Know when to call a professional"],
    explanation: [
      "AI is great for understanding terms, preparing questions and summarizing documents before an appointment. It is not a doctor, financial advisor or lawyer, and it can't see your full situation.",
      "Use it to get ready: 'Explain this lab result in plain words and list questions I should ask my doctor.' Then make the decision with a qualified person.",
    ],
    example: "Before a mortgage meeting you ask AI to explain APR vs interest rate and draft five questions. You bring the questions; the lender answers them for your situation.",
    check: mc("single", "What's the safest way to use AI for a medical question?", ["Follow its treatment advice", "*Use it to understand terms and prepare questions for your doctor", "Avoid telling your doctor you used AI"], "AI helps you prepare; a professional decides."),
    takeaway: "Prepare with AI, decide with a professional.",
    tool: chat,
    resource: guide,
    meta: meta("review-annually", [NIST]),
  },
  {
    slug: "workplace-ai-policy",
    title: "Following Your Workplace AI Rules",
    minutes: 4,
    difficulty: "Intermediate",
    learn: ["Find out which AI tools are approved", "Know what data is off-limits"],
    explanation: [
      "Many employers now have rules about which AI tools you can use and what you can put into them. Using an unapproved tool with company data can break contracts or privacy law, even with good intentions.",
      "Ask three questions: Which tools are approved? What data can never go in? Do I need to disclose AI help? When in doubt, remove names and numbers or ask first.",
    ],
    example: "You want to summarize a client contract. Your company approves one enterprise AI tool, so you use that one — not your personal account.",
    check: tf("If a free AI tool is useful, it's fine to paste company data into it.", false, "Use only approved tools for work data and follow your organization's rules."),
    takeaway: "Approved tool, allowed data, disclose when required.",
    tool: guide,
    resource: blogs,
    meta: meta("review-annually", [NIST]),
  },
]);

export const CYBERSECURITY_EVERYDAY = lessonsFor("cybersecurity", [
  {
    slug: "password-managers",
    title: "Password Managers Made Simple",
    minutes: 4,
    learn: ["Why one strong password per account matters", "How a password manager does the remembering"],
    explanation: [
      "Reusing passwords means one breach unlocks many accounts. A password manager creates and remembers a unique, strong password for every site — you remember one strong master passphrase.",
      "Protect the manager with MFA, and use its autofill: it only fills on the real site, which also helps you notice look-alike phishing pages.",
    ],
    example: "Your manager refuses to autofill on 'paypa1-login.com'. That's your clue the site is fake.",
    check: mc("single", "What's a hidden security benefit of password-manager autofill?", ["It types faster", "*It won't fill on look-alike phishing sites", "It shares passwords with friends"], "Autofill matches the real domain, so a fake site gets nothing."),
    takeaway: "Unique passwords everywhere, one strong passphrase, MFA on the manager.",
    tool: pw,
    resource: checklist,
    meta: meta("review-annually", [CISA]),
  },
  {
    slug: "public-wifi-and-travel",
    title: "Staying Safe on Public Wi-Fi and While Traveling",
    minutes: 4,
    learn: ["Spot fake hotspots", "Protect devices on the road"],
    explanation: [
      "Attackers set up hotspots with names like 'Airport_Free_WiFi'. Confirm the official network name with staff, prefer your phone's hotspot for banking, and keep devices updated.",
      "Turn on device lock and find-my-device, avoid public USB charging ports with data cables, and be wary of 'your package is held' texts while you travel.",
    ],
    example: "Two networks appear: 'Hotel Guest' and 'Hotel Guest Free'. You ask the front desk which is real before connecting.",
    check: tf("A Wi-Fi network named after the café is guaranteed to be the café's.", false, "Anyone can name a hotspot anything. Confirm with staff."),
    takeaway: "Confirm the network, prefer your hotspot for sensitive tasks, keep devices locked and updated.",
    tool: checklist,
    resource: blogs,
    meta: meta("review-annually", [CISA, FTC]),
  },
]);

export const PROMPTING_EVERYDAY = lessonsFor("prompting", [
  {
    slug: "notes-to-action-items",
    title: "Turn Messy Notes Into Action Items",
    minutes: 4,
    learn: ["A reusable prompt for meeting notes", "Keep owners and dates accurate"],
    explanation: [
      "Paste your notes and ask: 'List decisions, action items (owner, due date), and open questions. If an owner or date isn't in the notes, write \"unassigned\" — don't guess.'",
      "That last line matters: without it, AI may invent owners and deadlines that sound right. Review the list before sending it to anyone.",
    ],
    example: "The AI returns 'Draft budget — owner: unassigned — due: Friday', prompting you to assign it before the recap goes out.",
    check: mc("single", "Why add 'write unassigned instead of guessing'?", ["It makes output shorter", "*It stops the AI inventing owners or dates", "It's required by the tool"], "Missing facts should stay visibly missing."),
    takeaway: "Ask for decisions, actions and questions — and forbid guessed owners or dates.",
    tool: studio,
    resource: blogs,
    meta: meta("review-annually", [OPENAI]),
  },
  {
    slug: "prompting-for-images",
    title: "Prompting for Images",
    minutes: 5,
    difficulty: "Intermediate",
    learn: ["Describe subject, style, composition and light", "Respect people and brands"],
    explanation: [
      "Good image prompts cover four things: the subject (what and who), the style (photo, watercolor, flat icon), composition (close-up, wide, top-down) and lighting or mood.",
      "Avoid generating real people without consent, logos you don't own, or anything that could mislead. Label AI images when you share them.",
    ],
    example: "Weak: 'a dog'. Strong: 'A golden retriever puppy on a porch, soft morning light, shallow depth of field, warm photographic style, wide shot.'",
    check: mc("single", "Which element is missing from 'a watercolor of a lighthouse'?", ["Style", "Subject", "*Composition and lighting", "Nothing"], "Style and subject are there; composition and light make it specific."),
    takeaway: "Subject, style, composition, light — and label what you share.",
    tool: studio,
    resource: blogs,
    meta: meta("fast-changing", [OPENAI]),
  },
]);

export const AI_FLUENCY_EVERYDAY = lessonsFor("ai-fluency", [
  {
    slug: "tokens-and-limits",
    title: "Tokens, Length Limits and Why AI Forgets",
    minutes: 5,
    difficulty: "Intermediate",
    learn: ["What a token is", "Why long chats lose early details"],
    explanation: [
      "AI reads text in small chunks called tokens (roughly three-quarters of a word). Each model can only hold so many at once — its context window.",
      "In very long chats, early details can fall out or get less attention. Restate key facts, start a fresh chat for a new task, or paste a short summary to carry context forward.",
    ],
    example: "Forty messages into planning a trip, the AI forgets your budget. You start a new chat with a three-line summary and it's back on track.",
    check: tf("An AI chat remembers everything from the start no matter how long it gets.", false, "Context windows are limited; long chats can lose early details."),
    takeaway: "Long chats fade — restate key facts or start fresh with a summary.",
    tool: chat,
    resource: blogs,
    meta: meta("fast-changing", [NIST]),
  },
  {
    slug: "ai-and-your-job",
    title: "AI and Your Job: Tasks, Not Titles",
    minutes: 4,
    learn: ["Break your work into tasks", "Find where AI helps and where you add value"],
    explanation: [
      "AI rarely replaces a whole job; it changes tasks. List your weekly tasks and sort them: AI can draft it, AI can assist, or it needs me (judgment, relationships, accountability).",
      "Use AI on the first two to free time for the third — that's where your value grows.",
    ],
    example: "A teacher lets AI draft quiz questions and parent emails, then spends the saved time on one-to-one feedback.",
    check: mc("single", "Which task most needs a human?", ["Formatting a report", "First draft of a summary", "*Delivering difficult feedback to a colleague"], "Judgment and relationships stay human."),
    takeaway: "Sort tasks into draft, assist, and human — then invest in the human part.",
    tool: chat,
    resource: blogs,
    meta: meta("review-annually", [NIST]),
  },
]);
